import React, { useState } from 'react';
import { format } from 'date-fns';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MagnifyingGlass, Trash, ToggleRight, ToggleLeft, Key } from '@phosphor-icons/react';
import AdminResetPasswordDialog from './AdminResetPasswordDialog';

const UsersView = ({ users, searchQuery, setSearchQuery, handleUpdateUserRole, handleDeleteUser, handleToggleUserStatus, currentUser }) => {
  const [resetTarget, setResetTarget] = useState(null);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7a99]" size={16} />
          <Input placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-[#161b26] border-[#252d3d] text-white rounded-xl h-9 text-sm" data-testid="search-users" />
        </div>
        <span className="text-[#6b7a99] text-xs">{users.length} users</span>
      </div>
      <div className="bg-[#161b26] rounded-2xl border border-[#252d3d] overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-[#252d3d]">
            <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">User</th>
            <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Email</th>
            <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Role</th>
            <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Status</th>
            <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Joined</th>
            <th className="text-right py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Actions</th>
          </tr></thead>
          <tbody>{users.map(u => (
            <tr key={u.user_id} className={`border-b border-[#252d3d]/50 hover:bg-[#1e2536]/50 ${u.active === false ? 'opacity-50' : ''}`}>
              <td className="py-2.5 px-4 text-white text-sm font-medium">
                {u.name}
                {u.must_change_password && (
                  <span className="ml-2 text-[9px] uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-400/30 px-1.5 py-0.5 rounded-full" title="User has a pending password change">pending change</span>
                )}
              </td>
              <td className="py-2.5 px-4 text-[#a5b4d0] text-sm">{u.email}</td>
              <td className="py-2.5 px-4">
                <Select value={u.role} onValueChange={v => handleUpdateUserRole(u.user_id, v)}>
                  <SelectTrigger className="w-24 h-7 text-xs bg-[#0f1219] border-[#252d3d] text-white rounded-lg" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="buyer">Buyer</SelectItem><SelectItem value="seller">Seller</SelectItem><SelectItem value="both">Both</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
              </td>
              <td className="py-2.5 px-4">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${u.active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {u.active !== false ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="py-2.5 px-4 text-[#6b7a99] text-xs">{u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : '-'}</td>
              <td className="py-2.5 px-4 text-right flex items-center justify-end gap-1">
                <button
                  onClick={() => setResetTarget(u)}
                  disabled={u.email === currentUser.email}
                  title="Regenerate password"
                  className="p-1.5 text-[#6b7a99] hover:text-amber-400 hover:bg-amber-500/10 rounded-lg disabled:opacity-20"
                  data-testid={`reset-password-${u.user_id}`}>
                  <Key size={14} weight="fill" />
                </button>
                <button onClick={() => handleToggleUserStatus(u.user_id)} disabled={u.email === currentUser.email}
                  title={u.active !== false ? 'Deactivate User' : 'Activate User'}
                  className="p-1 disabled:opacity-20" data-testid={`toggle-user-${u.user_id}`}>
                  {u.active !== false ? <ToggleRight size={20} className="text-emerald-400" weight="fill" /> : <ToggleLeft size={20} className="text-[#6b7a99]" weight="fill" />}
                </button>
                <button onClick={() => handleDeleteUser(u.user_id)} disabled={u.email === currentUser.email} className="p-1.5 text-[#6b7a99] hover:text-red-400 hover:bg-red-500/10 rounded-lg disabled:opacity-20" data-testid={`delete-user-${u.user_id}`}><Trash size={16} /></button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <AdminResetPasswordDialog
        user={resetTarget}
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
      />
    </div>
  );
};

export default UsersView;
